import React from "react";
import { useState, useCallback, useMemo } from "react";
import { useNamingTagConfig } from "@/hooks/useNamingTagConfig";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Plus, X, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

export interface NamingConventionValue {
  tags: string[];
  separator: string;
  custom_separator: string;
  custom_tags: string[];
}

interface NamingConventionBuilderProps {
  value: NamingConventionValue;
  onChange: (value: NamingConventionValue) => void;
  isLoading?: boolean;
}

const SEPARATORS = [
  { label: "Underscore", value: "_" },
  { label: "Hyphen", value: "-" },
  { label: "Pipe", value: "|" },
  { label: "Double", value: "__" },
  { label: "Ampersand", value: "&" },
  { label: "Custom", value: "custom" },
];

export function NamingConventionBuilder({
  value,
  onChange,
  isLoading: externalLoading,
}: NamingConventionBuilderProps) {
  const { data: tagDefs, isLoading: tagsLoading, isError } = useNamingTagConfig();
  const [customInput, setCustomInput] = useState("");
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const loading = externalLoading || tagsLoading;

  const activeSeparator =
    value.separator === "custom" ? value.custom_separator || "_" : value.separator || "_";

  // Synthetic date variant definitions (injected client-side alongside DB tags)
  const DATE_VARIANTS: Array<{ tag_key: string; label: string; tag_group: string; preview_example: string }> = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return [
      { tag_key: "date_us", label: "Date US", tag_group: "dynamic", preview_example: `${m}${day}${y}` },
      { tag_key: "date_int", label: "Date INT", tag_group: "dynamic", preview_example: `${day}${m}${y}` },
    ];
  }, []);

  const DATE_TAG_KEYS = useMemo(() => new Set(["date", "date_us", "date_int"]), []);

  const dynamicTags = useMemo(() => {
    const dbDynamic = (tagDefs ?? []).filter((t) => t.tag_group === "dynamic");
    // Insert date_us and date_int right after the existing "date" tag
    const result: typeof dbDynamic = [];
    for (const t of dbDynamic) {
      result.push(t);
      if (t.tag_key === "date") {
        for (const v of DATE_VARIANTS) result.push(v as any);
      }
    }
    return result;
  }, [tagDefs, DATE_VARIANTS]);

  const placeholderTags = useMemo(
    () => (tagDefs ?? []).filter((t) => t.tag_group === "placeholder"),
    [tagDefs],
  );

  const tagDefMap = useMemo(() => {
    const m = new Map<string, (typeof tagDefs extends (infer T)[] | undefined ? T : never)>();
    for (const t of tagDefs ?? []) m.set(t.tag_key, t);
    // Override date preview to always use current date (not stale DB value)
    const existing = m.get("date");
    if (existing) {
      m.set("date", { ...existing, preview_example: new Date().toISOString().slice(0, 10).replace(/-/g, "") });
    }
    // Add synthetic date variants
    for (const v of DATE_VARIANTS) m.set(v.tag_key, v as any);
    return m;
  }, [tagDefs, DATE_VARIANTS]);

  const update = useCallback(
    (partial: Partial<NamingConventionValue>) => {
      onChange({ ...value, ...partial });
    },
    [value, onChange],
  );

  const addTag = (key: string) => {
    if (value.tags.includes(key)) return;
    // Mutual exclusion: only one date tag at a time
    if (DATE_TAG_KEYS.has(key)) {
      const filtered = value.tags.filter((t) => !DATE_TAG_KEYS.has(t));
      update({ tags: [...filtered, key] });
      return;
    }
    update({ tags: [...value.tags, key] });
  };

  const removeTag = (key: string) => {
    const next = value.tags.filter((t) => t !== key);
    if (key.startsWith("custom:")) {
      const plain = key.slice(7);
      update({
        tags: next,
        custom_tags: value.custom_tags.filter((c) => c !== plain),
      });
    } else {
      update({ tags: next });
    }
  };

  const addCustomTag = () => {
    const text = customInput.trim();
    if (!text || value.custom_tags.length >= 5) return;
    if (value.custom_tags.includes(text)) return;
    const key = `custom:${text}`;
    update({
      tags: [...value.tags, key],
      custom_tags: [...value.custom_tags, text],
    });
    setCustomInput("");
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx: number) => setDragIndex(idx);
  const handleDragEnter = (idx: number) => {
    if (dragIndex === null || dragIndex === idx) return;
    const reordered = [...value.tags];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(idx, 0, moved);
    setDragIndex(idx);
    update({ tags: reordered });
  };
  const handleDragEnd = () => setDragIndex(null);

  // Preview
  const patternLine = value.tags
    .map((t) => (t.startsWith("custom:") ? t.slice(7) : `{${t}}`))
    .join(activeSeparator);

  const exampleLine = value.tags
    .map((t) => {
      if (t.startsWith("custom:")) return t.slice(7);
      const def = tagDefMap.get(t);
      return def?.preview_example ?? t;
    })
    .join(activeSeparator);

  const charCount = exampleLine.length;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  }

  if (isError || !tagDefs) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Naming tag configuration could not be loaded. Please refresh the page.
      </div>
    );
  }

  const renderChip = (
    key: string,
    label: string,
    selected: boolean,
    onClick?: () => void,
  ) => (
    <Badge
      key={key}
      variant={selected ? "secondary" : "outline"}
      className={cn(
        "cursor-pointer select-none transition-colors",
        selected && "opacity-50 cursor-default",
      )}
      onClick={selected ? undefined : onClick}
    >
      {label}
    </Badge>
  );

  return (
    <div className="space-y-4">
      {/* Available Tags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Available Tags</CardTitle>
          <CardDescription className="text-xs">
            Click a tag to add it to your naming pattern.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Dynamic</p>
            <div className="flex flex-wrap gap-1.5">
              {dynamicTags.map((t) =>
                renderChip(
                  t.tag_key,
                  t.label,
                  value.tags.includes(t.tag_key),
                  () => addTag(t.tag_key),
                ),
              )}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Placeholders</p>
            <div className="flex flex-wrap gap-1.5">
              {placeholderTags.map((t) =>
                renderChip(
                  t.tag_key,
                  t.label,
                  value.tags.includes(t.tag_key),
                  () => addTag(t.tag_key),
                ),
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Tags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Custom Tags</CardTitle>
          <CardDescription className="text-xs">
            Add up to 5 custom text segments ({value.custom_tags.length}/5).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-2">
            <Input
              placeholder="e.g. summer-sale"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomTag())}
              className="flex-1"
              maxLength={40}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addCustomTag}
              disabled={!customInput.trim() || value.custom_tags.length >= 5}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          </div>
          {value.custom_tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {value.custom_tags.map((ct) => (
                <Badge key={ct} variant="secondary" className="gap-1">
                  {ct}
                  <button
                    type="button"
                    onClick={() => removeTag(`custom:${ct}`)}
                    className="ml-0.5 hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Tags (reorderable) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Selected Tags</CardTitle>
          <CardDescription className="text-xs">
            Drag to reorder. Click × to remove.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {value.tags.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No tags selected. Add tags from above.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {value.tags.map((tag, idx) => {
                const isCustom = tag.startsWith("custom:");
                const label = isCustom
                  ? tag.slice(7)
                  : tagDefMap.get(tag)?.label ?? tag;

                return (
                  <Badge
                    key={`${tag}-${idx}`}
                    variant="default"
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    className={cn(
                      "cursor-grab active:cursor-grabbing gap-1 select-none",
                      dragIndex === idx && "opacity-50",
                    )}
                  >
                    <GripVertical className="h-3 w-3 opacity-50" />
                    {label}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-0.5 hover:text-destructive-foreground/70 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Separator */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Separator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {SEPARATORS.map((s) => (
              <Button
                key={s.value}
                type="button"
                size="sm"
                variant={value.separator === s.value ? "default" : "outline"}
                onClick={() => update({ separator: s.value })}
              >
                {s.label}
                {s.value !== "custom" && (
                  <span className="ml-1 font-mono text-xs opacity-70">
                    {s.value}
                  </span>
                )}
              </Button>
            ))}
          </div>
          {value.separator === "custom" && (
            <Input
              placeholder="Enter custom separator"
              value={value.custom_separator}
              onChange={(e) => update({ custom_separator: e.target.value })}
              className="max-w-48"
              maxLength={5}
            />
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      {value.tags.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-0.5">Pattern</p>
              <p className="text-sm font-mono bg-muted rounded px-2 py-1 break-all">
                {patternLine}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-0.5">Example</p>
              <p className="text-sm font-mono bg-muted rounded px-2 py-1 break-all">
                {exampleLine}
              </p>
            </div>
            <p
              className={cn(
                "text-xs",
                charCount > 255
                  ? "text-destructive font-medium"
                  : charCount > 150
                    ? "text-yellow-600"
                    : "text-muted-foreground",
              )}
            >
              {charCount} characters
              {charCount > 255 && " — exceeds 255 character limit"}
              {charCount > 150 && charCount <= 255 && " — approaching limit"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
